/**
 * "View as": see the app exactly as another person's department grants scope it, to
 * check what someone will see before they are given access. Read-only, full-access
 * callers only, audited by the API (api/shared/viewAs.ts; docs/ACCESS.md).
 *
 * Two pieces:
 *  - ViewAsControl, in the masthead: "View as…" with a search over everyone who holds
 *    a grant, or any email typed in. For callers holding full access AND the view_as
 *    permission, whether or not Dev mode is on (src/ui/devMode.tsx) — the API, not
 *    this file, is what refuses everyone else.
 *  - ViewAsBanner, above everything: a red bar for as long as the tab is viewing as
 *    someone, with Exit. Outside the dataset gate, so Exit is there even when the view
 *    failed to load (an audit failure, say).
 */
import { useEffect, useRef, useState } from 'react'
import { IconEye } from '@tabler/icons-react'
import { useAuth } from '@/auth/AuthProvider'
import { TOKEN_HEADER } from '@/data/client'
import { apiFetch } from '@/auth/apiFetch'
import { useDataset } from '@/data/DatasetProvider'
import { exitViewAs, looksLikeEmail, startViewAs, viewingAs } from '@/data/viewAs'
import { useMediaQuery } from '@/ui/useMediaQuery'
import { shownTools, useDevMode } from '@/ui/devMode'
import { SearchPick, type PickOption } from './SearchPick'

/** Grant holders for the picker, or null when the caller may not view as anyone. */
function useViewAsTargets(enabled: boolean): string[] | null {
  const { token, ready } = useAuth()
  const [targets, setTargets] = useState<string[] | null>(null)
  useEffect(() => {
    if (!ready || !enabled) {
      setTargets(null)
      return
    }
    let cancelled = false
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers[TOKEN_HEADER] = token
    apiFetch('/api/view-as', { headers })
      .then((r) => (r.ok ? (r.json() as Promise<{ targets?: unknown }>) : null))
      .then((body) => {
        if (cancelled) return
        const list = Array.isArray(body?.targets)
          ? body.targets.filter((t): t is string => typeof t === 'string')
          : null
        setTargets(list)
      })
      // 403 (not full access), 404 (an older API) or anything else: no control.
      .catch(() => !cancelled && setTargets(null))
    return () => {
      cancelled = true
    }
  }, [token, ready, enabled])
  return targets
}

const typedEmail = (q: string): PickOption | null => {
  const v = q.trim().toLowerCase()
  return looksLikeEmail(v) ? { value: v, label: `${v} (no grant row)` } : null
}

export function ViewAsControl() {
  const dev = useDevMode()
  const targets = useViewAsTargets(shownTools(dev.features, dev.on).viewAs)
  const wide = useMediaQuery('(min-width: 900px)')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!targets) return null
  const current = viewingAs()
  const options = targets.map((t) => ({ value: t, label: t }))

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="See the app as someone else's grants scope it (read-only)"
        aria-label="View as someone else"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...(wide ? {} : { padding: '5px 7px' }) }}
      >
        <IconEye size={15} stroke={1.6} aria-hidden />
        {wide && 'View as…'}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="View as"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 'min(320px, calc(100vw - 32px))',
            background: 'var(--surface)',
            border: '1px solid var(--rule)',
            borderRadius: 10,
            boxShadow: 'var(--shadow)',
            padding: 14,
            display: 'grid',
            gap: 10,
            zIndex: 60,
          }}
        >
          <SearchPick
            label="View as"
            value={current ?? ''}
            options={options}
            placeholder="Pick a person or type an email"
            custom={typedEmail}
            onChange={(v) => startViewAs(v)}
          />
          <p style={{ margin: 0, fontSize: 'var(--t-rank)', color: 'var(--ink-3)', lineHeight: 1.45 }}>
            Read-only. Shows what their rows in RefUserDepartmentSecurity grant — not
            full-access group membership, which the app can't see. Every start is logged.
          </p>
        </div>
      )}
    </div>
  )
}

export function ViewAsBanner() {
  const target = viewingAs()
  const { dataset, loading } = useDataset()
  if (!target) return null
  const empty = !loading && dataset !== null && (dataset.employees?.length ?? 0) === 0
  return (
    <div
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 200,
        background: 'var(--crit)',
        color: '#fff',
        padding: '7px 16px',
        fontSize: 13,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px 12px',
      }}
    >
      <IconEye size={16} stroke={1.8} aria-hidden style={{ flex: 'none' }} />
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        <strong>Viewing as {target}</strong> — read-only · as their grants; full-access
        group membership isn't simulated
        {empty && <> · <strong>they have no department grants, so they see nothing</strong></>}
      </span>
      <button
        type="button"
        onClick={exitViewAs}
        style={{
          background: '#fff',
          color: 'var(--crit-ink)',
          border: 0,
          borderRadius: 6,
          padding: '3px 12px',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Exit
      </button>
    </div>
  )
}
